"use client";

import { clearAuthCookies, hasValidAuthToken } from "@/lib/axios";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";

interface User {
  id: string;
  name: string;
  surname: string;
  fathername: string;
  fiNcode: string;
  email: string;
  isOwner: boolean;
  isLegitimateUser: boolean;
  isActive: boolean;
  companyId: number;
  companyName: string;
  phones: any[];
  // Doc 4.4: natamam profil 403 torunu idarə etmək üçün
  voen?: string;
  isProfileComplete?: boolean;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isLoggedIn: boolean;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  // Keçərli auth-token olub-olmadığı (qeydiyyat yarımçıq qalanda token olmur)
  const [hasToken, setHasToken] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    // Try to get user from localStorage (if stored there)
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Error parsing stored user:", error);
        localStorage.removeItem("user");
      }
    }
    setHasToken(hasValidAuthToken());
  }, []);

  const handleSetUser = (newUser: User | null) => {
    setUser(newUser);
    if (newUser) {
      localStorage.setItem("user", JSON.stringify(newUser));
    } else {
      localStorage.removeItem("user");
    }
    // user dəyişəndə token vəziyyətini yenidən yoxla
    setHasToken(hasValidAuthToken());
  };

  const logout = () => {
    setUser(null);
    setHasToken(false);
    localStorage.removeItem("user");
    // Clear HttpOnly cookies using our utility function
    clearAuthCookies();
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: handleSetUser,
        // Həm user, həm də keçərli token olmalıdır — yarımçıq qeydiyyatda ad yazılmasın
        isLoggedIn: !!user && hasToken,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
